import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  X,
  FileText,
  User,
  Calendar,
  Clock,
  AlertCircle,
  RefreshCw,
  Pause,
  Play,
  CheckCircle,
  AlertTriangle,
  Mail,
  Phone,
  Building,
  Send,
} from 'lucide-react';
import { invoicesApi, Invoice, InvoiceDetail } from '../api/invoices.api';
import { InvoicePdfActions } from '../components/InvoicePdfActions';
import { APP_DETAIL_OVERLAY_CLASS, APP_MAIN_OVERLAY_CLASS, APP_PAGE_WRAPPER_CLASS } from '../lib/layout';

// Due Status Badge Component
function DueStatusBadge({ status }: { status: string }) {
  const configs: Record<string, { label: string; icon: React.ElementType; style: string }> = {
    paid: {
      label: 'Paid',
      icon: CheckCircle,
      style: 'bg-green-100 text-green-800 border-green-200',
    },
    due_today: {
      label: 'Due Today',
      icon: Clock,
      style: 'bg-orange-100 text-orange-800 border-orange-200',
    },
    overdue: {
      label: 'Overdue',
      icon: AlertTriangle,
      style: 'bg-red-100 text-red-800 border-red-200',
    },
    upcoming: {
      label: 'Upcoming',
      icon: Calendar,
      style: 'bg-blue-100 text-blue-800 border-blue-200',
    },
    unknown: {
      label: 'Unknown',
      icon: AlertCircle,
      style: 'bg-gray-100 text-gray-600 border-gray-200',
    },
  };

  const config = configs[status] || configs.unknown;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${config.style}`}>
      <Icon className="w-4 h-4" />
      {config.label}
    </span>
  );
}

// Payment Status Badge Component
function PaymentStatusBadge({ status }: { status: string }) {
  const labels: Record<string, string> = {
    unpaid: 'Unpaid',
    partially_paid: 'Partially Paid',
    paid: 'Paid',
    cancelled: 'Cancelled',
    unknown: 'Unknown',
  };

  const styles: Record<string, string> = {
    unpaid: 'bg-red-100 text-red-700 border-red-200',
    partially_paid: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    paid: 'bg-green-100 text-green-700 border-green-200',
    cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
    unknown: 'bg-gray-100 text-gray-500 border-gray-200',
  };

  return (
    <span className={`inline-flex px-3 py-1.5 rounded-full text-sm font-medium border ${styles[status] || styles.unknown}`}>
      {labels[status] || 'Unknown'}
    </span>
  );
}

// Info Row Component
function InfoRow({
  icon: Icon,
  label,
  value,
  href,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  href?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-5 h-5 text-gray-400 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm text-gray-500">{label}</p>
        {href ? (
          <a
            href={href}
            className="text-gray-900 hover:text-blue-600 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {value}
          </a>
        ) : (
          <p className="text-gray-900">{value}</p>
        )}
      </div>
    </div>
  );
}

// Section Component
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      <h3 className="text-sm font-medium text-gray-900 uppercase tracking-wide mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}

export function InvoiceDetailsDrawer({
  invoice,
  isOpen,
  onClose,
  overlayClass = APP_DETAIL_OVERLAY_CLASS,
}: {
  invoice: Invoice | null;
  isOpen: boolean;
  onClose: () => void;
  overlayClass?: string;
}) {
  const queryClient = useQueryClient();

  // Fetch detailed invoice data
  const { data: invoiceDetail, isLoading } = useQuery<InvoiceDetail>({
    queryKey: ['invoice', invoice?.id],
    queryFn: () => invoicesApi.getInvoice(invoice!.id),
    enabled: !!invoice && isOpen,
  });

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: () => invoicesApi.syncInvoice(invoice!.id),
    onSuccess: () => {
      toast.success('Invoice synced successfully');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', invoice?.id] });
    },
    onError: () => {
      toast.error('Failed to sync invoice');
    },
  });

  // Toggle pause mutation
  const togglePauseMutation = useMutation({
    mutationFn: () => invoicesApi.togglePause(invoice!.id),
    onSuccess: (data) => {
      toast.success(data.reminder_paused ? 'Reminders paused' : 'Reminders resumed');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', invoice?.id] });
    },
    onError: () => {
      toast.error('Failed to toggle reminder pause');
    },
  });

  // Format currency
  const formatCurrency = (amount: number, currency: string = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (date: string | null, withTime: boolean = false) => {
    if (!date) return 'N/A';
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    };
    if (withTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }
    return new Date(date).toLocaleDateString('en-IN', options);
  };

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !invoice) return null;

  const detail = invoiceDetail || (invoice as unknown as InvoiceDetail);

  return (
    <div className={`${overlayClass} overflow-hidden`}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="absolute inset-y-0 right-0 max-w-2xl w-full bg-white shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Invoice {detail.invoice_number}
              </h2>
              <p className="text-sm text-gray-500">ID: {detail.crm_invoice_id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
              <span className="ml-3 text-gray-600">Loading details...</span>
            </div>
          ) : (
            <>
              {/* Status Badges */}
              <div className="flex flex-wrap gap-3 mb-6">
                <PaymentStatusBadge status={detail.payment_status} />
                <DueStatusBadge status={detail.due_status} />
                {detail.reminder_paused && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-orange-100 text-orange-800 border border-orange-200">
                    <Pause className="w-4 h-4" />
                    Reminders Paused
                  </span>
                )}
              </div>

              {/* Amount Summary */}
              <div className="grid grid-cols-3 gap-4 bg-gray-50 rounded-lg p-4 mb-6">
                <div>
                  <p className="text-sm text-gray-500">Total Amount</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {formatCurrency(detail.amount_total)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Amount Paid</p>
                  <p className="text-xl font-semibold text-green-600">
                    {formatCurrency(detail.amount_paid)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Outstanding</p>
                  <p className={`text-xl font-semibold ${detail.amount_outstanding > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {formatCurrency(detail.amount_outstanding)}
                  </p>
                </div>
              </div>

              {/* Dates */}
              <Section title="Invoice Dates">
                <div className="grid grid-cols-2 gap-4">
                  <InfoRow
                    icon={Calendar}
                    label="Invoice Date"
                    value={formatDate(detail.invoice_date)}
                  />
                  <InfoRow
                    icon={Calendar}
                    label="Due Date"
                    value={formatDate(detail.due_date)}
                  />
                  {detail.paid_at && (
                    <InfoRow
                      icon={CheckCircle}
                      label="Paid On"
                      value={formatDate(detail.paid_at, true)}
                    />
                  )}
                  {detail.cancelled_at && (
                    <InfoRow
                      icon={AlertCircle}
                      label="Cancelled On"
                      value={formatDate(detail.cancelled_at)}
                    />
                  )}
                  <InfoRow
                    icon={RefreshCw}
                    label="Last Synced"
                    value={formatDate(detail.last_synced_at, true)}
                  />
                </div>
              </Section>

              {/* Subscriber Info */}
              <Section title="Subscriber Information">
                <div className="space-y-3">
                  <InfoRow
                    icon={User}
                    label="Name"
                    value={detail.subscriber.name}
                  />
                  {detail.subscriber.email && (
                    <InfoRow
                      icon={Mail}
                      label="Email"
                      value={detail.subscriber.email}
                      href={`mailto:${detail.subscriber.email}`}
                    />
                  )}
                  {detail.subscriber.mobile && (
                    <InfoRow
                      icon={Phone}
                      label="Mobile"
                      value={detail.subscriber.mobile}
                      href={`tel:${detail.subscriber.mobile}`}
                    />
                  )}
                  {detail.subscriber.subscriber_code && (
                    <InfoRow
                      icon={Building}
                      label="Subscriber Code"
                      value={detail.subscriber.subscriber_code}
                    />
                  )}
                </div>
              </Section>

              {/* Reminder History */}
              {detail.reminders && detail.reminders.length > 0 && (
                <Section title="Reminder History">
                  <div className="space-y-2">
                    {detail.reminders.map((reminder) => (
                      <div
                        key={reminder.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <Send className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="font-medium text-gray-900">
                              {reminder.channel.toUpperCase()}
                            </p>
                            <p className="text-sm text-gray-500">
                              {reminder.status} • {formatDate(reminder.created_at, true)}
                            </p>
                          </div>
                        </div>
                        {reminder.delivered_at && (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        )}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* CRM Info */}
              <Section title="CRM Information">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">CRM Site ID</p>
                    <p className="font-medium">{detail.crm_site_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">CRM Subscriber ID</p>
                    <p className="font-medium">{detail.crm_subscriber_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">CRM Invoice ID</p>
                    <p className="font-medium">{detail.crm_invoice_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Raw Status</p>
                    <p className="font-medium">{detail.crm_status_raw}</p>
                  </div>
                </div>
              </Section>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-wrap gap-3">
            <InvoicePdfActions
              invoiceId={invoice.id}
              pdfUrl={detail.pdf_url}
              hasPdf={detail.has_pdf}
            />
            <button
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              Sync Now
            </button>
            <button
              onClick={() => togglePauseMutation.mutate()}
              disabled={togglePauseMutation.isPending}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                detail?.reminder_paused
                  ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              } disabled:opacity-50`}
            >
              {detail?.reminder_paused ? (
                <>
                  <Play className="w-4 h-4" />
                  Resume Reminders
                </>
              ) : (
                <>
                  <Pause className="w-4 h-4" />
                  Pause Reminders
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Standalone route for /invoices/:id */
export function InvoiceDetailsRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invoiceId = Number(id);

  const { data: invoice, isLoading } = useQuery<InvoiceDetail>({
    queryKey: ['invoice-route', id],
    queryFn: () => invoicesApi.getInvoice(invoiceId),
    enabled: !!id && !Number.isNaN(invoiceId),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className={APP_PAGE_WRAPPER_CLASS}>
      <InvoiceDetailsDrawer
        invoice={invoice ?? null}
        isOpen={true}
        onClose={() => navigate('/invoices')}
        overlayClass={APP_MAIN_OVERLAY_CLASS}
      />
    </div>
  );
}

export default InvoiceDetailsDrawer;
